/* eslint-disable quotes */
import { Component, Vue } from "vue-property-decorator";
import transitionModule from "@/store/modules/common/transition.module";
import { ROLE, TransitionType } from "@/config/enum";
import _ from "lodash";
import authModule from "@/store/modules/common/auth.module";
import { HOME, LOGIN } from "@/router";
import { RawLocation } from "vue-router/types/router";
import authService from "@/services/common/auth.service";

@Component
class Mixin extends Vue {
  constructor() {
    super();
  }

  get isAdmin() {
    return authModule.user?.role === ROLE.Administrator.toString();
  }

  get isLoginPage() {
    return this.$route.name === LOGIN;
  }

  get isHomePage() {
    return this.$route.name === HOME;
  }

  get showMenu() {
    return !(
      this.isHomePage || authModule.user?.role === ROLE.Manager.toString()
    );
  }

  public isSmallScreen() {
    return window.screen.width < 672;
  }

  public isMobile() {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        navigator.userAgent || navigator.vendor
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        (navigator.userAgent || navigator.vendor).substr(0, 4)
      )
    )
      return true;

    return false;
  }

  public async goBack(step?: number) {
    await transitionModule.updateMenuStatus(TransitionType.fade);
    this.$router.go(step || -1);
  }

  public async goHome() {
    await this.goTo("/");
  }

  public async goTo(location: RawLocation) {
    await transitionModule.updateMenuStatus(TransitionType.fade);
    await this.$router.push(location);
  }

  public compare(a: any, b: any): boolean {
    return _.isEqual(a, b);
  }

  public toBase64(file: File): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  public async gotoLoginPage() {
    await this.goTo("/auth/login");
  }

  public async logout() {
    await authService.logout();
    await authModule.logout();
    window.localStorage.clear();
    window.sessionStorage.clear();
    await this.gotoLoginPage();
  }

  public isCutOffScreen(element: Element) {
    const rect = element.getBoundingClientRect();
    if (rect.x + rect.width > window.outerWidth) return 1;
    if (rect.x < 0) return -1;
    return 0;
  }

  public makeInitPassword(length = 6) {
    const result: string[] = [];
    const characters =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz023456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      // lodash random is inclusive, so cap at length - 1 to never hit an
      // out-of-range index (which would append an empty char → short password).
      result.push(characters.charAt(_.random(0, charactersLength - 1)));
    }
    return result.join("");
  }

  public groupListByDateDesc(data: any[], key: string | number) {
    const result = data.sort((x: any, y: any) => {
      const dateA: any = new Date(x[key]);
      const dateB: any = new Date(y[key]);
      return dateB - dateA;
    });

    const listed: any = _.orderBy(
      _.mapValues(_.groupBy(result, key)),
      [key],
      ["desc"]
    );
    return listed.reverse();
  }

  public decodingURLQueries(): Record<string, any> {
    // Using lodash here to loop through object...
    const decode = _.forOwn(this.$route.query, function(value: any, key: any) {
      const encodedValue = encodeURIComponent(value); // Let's pretend the values were encoded...
      const decodedValue = decodeURIComponent(encodedValue);
      return { key: key, value: decodedValue };
    }) as Record<string, any>;
    return decode;
  }
}

export default Mixin;
